/**
 * [2026-09-28] 알림창 키보드 규칙 — 공용 알림창(components/common/Modal.jsx)이 브라우저 기본 알림창처럼 키보드로 동작하게 한다.
 *
 * WHY: 브라우저 기본 알림창은 떠 있는 동안 페이지 입력을 막고 Enter·Esc 로 닫혔다. 안내를 사이트 알림창으로 옮긴 뒤(W63)
 *   알림창이 초점을 가져가지 않아 초점이 방금 누른 페이지 버튼(예: 충전신청 "신청")에 그대로 남았다. 그래서 Enter 를 누르면
 *   알림창은 그대로이고 뒤의 버튼이 한 번 더 눌렸고(2026-09-28 tosino ur2 실측 — 안내가 2개 쌓임), Esc 로도 닫히지 않았다.
 *
 * 규칙(열려 있는 알림창 중 맨 위 하나에만 적용):
 *   - 열리면 초점을 알림창의 첫 버튼("확인")으로 옮긴다 → Enter·Space 는 그 버튼을 누른다(버튼의 기본 동작 그대로).
 *   - Esc = 닫기(확인창이면 "취소").
 *   - Tab 은 알림창 버튼 사이에서만 돈다.
 *   - 그 밖의 키가 알림창 밖(페이지)으로 가려 하면 막고 초점을 "확인"으로 되돌린다 — 페이지 버튼·입력칸에 닿지 않는다.
 *   - 닫히면 열기 전 초점 자리로 돌려준다(브라우저 기본 알림창과 같다 — 입력하던 칸으로 돌아간다).
 */
import { useEffect, useRef } from 'react';

// 열려 있는 알림창(마지막 = 맨 위). 겹쳐 열리면 맨 위 하나만 키를 받는다.
const openDialogs = [];

const buttonsIn = (container) => Array.from(container.querySelectorAll('button:not([disabled])'));

/**
 * @param {boolean} isOpen 알림창이 열려 있는가
 * @param {{ current: HTMLElement | null }} containerRef 알림창 맨 바깥 요소
 * @param {Function} onCancel Esc 를 눌렀을 때 부를 함수(닫기·확인창의 취소)
 * @param {string} contentKey 내용이 바뀌었는지 가르는 값 — 다음 알림으로 넘어가 버튼 모양이 바뀌어도 초점을 "확인"에 둔다
 */
export function useDialogKeyboard(isOpen, containerRef, onCancel, contentKey) {
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  // 열림·닫힘 — 맨 위 등록, 키 가로채기(캡처 단계라 페이지 처리기보다 먼저), 닫힐 때 초점 복귀
  useEffect(() => {
    if (!isOpen) return undefined;
    const token = {};
    openDialogs.push(token);
    const previous = document.activeElement;
    // 열린 순간의 알림창 요소 — 닫힐 때는 React 가 ref 를 먼저 비우므로 정리 단계에서 쓸 요소를 미리 잡아 둔다
    const openedNode = containerRef.current;

    const onKeyDown = (event) => {
      if (openDialogs[openDialogs.length - 1] !== token) return;
      const container = containerRef.current;
      if (!container) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        event.stopPropagation();
        if (typeof cancelRef.current === 'function') cancelRef.current();
        return;
      }
      const buttons = buttonsIn(container);
      if (event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        if (buttons.length === 0) return;
        const at = buttons.indexOf(document.activeElement);
        const next = event.shiftKey
          ? (at <= 0 ? buttons.length - 1 : at - 1)
          : (at === -1 || at === buttons.length - 1 ? 0 : at + 1);
        buttons[next].focus();
        return;
      }
      if (!container.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        if (buttons[0]) buttons[0].focus();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      const at = openDialogs.indexOf(token);
      if (at !== -1) openDialogs.splice(at, 1);
      // 초점이 사라지는 알림창 안이나 빈 곳에 있을 때만 되돌린다(그사이 사용자가 다른 곳을 눌렀으면 그대로 둔다)
      const active = document.activeElement;
      const lost = !active || active === document.body || (openedNode && openedNode.contains(active));
      if (lost && previous && previous !== document.body && previous.isConnected && typeof previous.focus === 'function') {
        previous.focus();
      }
    };
  }, [isOpen, containerRef]);

  // 처음 열릴 때와 내용이 바뀔 때마다 — 초점이 알림창 밖이면 "확인"으로
  useEffect(() => {
    if (!isOpen) return;
    const container = containerRef.current;
    if (!container || container.contains(document.activeElement)) return;
    const first = buttonsIn(container)[0];
    if (first) first.focus();
  }, [isOpen, containerRef, contentKey]);
}

/** 시험 전용 — 열린 알림창 목록을 비운다. */
export function _resetForTest() {
  openDialogs.length = 0;
}
